import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import { vars } from '@/theme';

export interface DebugDetailsProps {
  /** Formatted report string (output of buildErrorReport) */
  report: string;
  /** Whether the panel starts expanded. Default: false */
  defaultExpanded?: boolean;
}

/**
 * Collapsible debug details panel with copy button.
 *
 * Uses <details>/<summary> for progressive enhancement —
 * works without JS and is safe to render even when React is unstable.
 * All styling is inline (no theme dependency) so it renders correctly
 * even when the VE theme is unavailable.
 */
export function DebugDetails({ report, defaultExpanded = false }: DebugDetailsProps) {
  const { copy, copied } = useCopyToClipboard();

  const handleCopy = () => copy(report);

  return (
    <details
      open={defaultExpanded || undefined}
      style={{ width: '100%', marginTop: 16 }}
      data-testid="debug-details"
    >
      <summary
        style={{
          cursor: 'pointer',
          fontSize: 13,
          color: vars.color.neutral.base,
          userSelect: 'none',
          padding: '4px 0',
        }}
        data-testid="debug-details-toggle"
      >
        Show error details
      </summary>

      <div
        style={{
          marginTop: 8,
          position: 'relative',
        }}
      >
        <button
          type="button"
          onClick={handleCopy}
          data-testid="debug-details-copy"
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            padding: '4px 12px',
            fontSize: 12,
            fontFamily: 'inherit',
            border: `1px solid ${vars.color.surface.border}`,
            borderRadius: 0,
            background: copied ? '#e8f5e9' : vars.color.fixed.white,
            color: copied ? '#2e7d32' : '#333',
            cursor: 'pointer',
          }}
        >
          {copied ? 'Copied!' : 'Copy'}
        </button>

        <pre
          data-testid="debug-details-content"
          style={{
            margin: 0,
            padding: 16,
            paddingTop: 12,
            background: vars.color.surface.muted,
            border: `1px solid ${vars.color.surface.border}`,
            borderRadius: 0,
            fontFamily: 'monospace',
            fontSize: 12,
            lineHeight: 1.5,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            maxHeight: 300,
            overflowY: 'auto',
            userSelect: 'text',
          }}
        >
          {report}
        </pre>
      </div>
    </details>
  );
}
